import { useState } from 'react'

import { F0Button } from '@factorialco/f0-react'
import { Check } from '@factorialco/f0-react/icons/app'
import { ProductUpdatesPoliciesDocument } from 'generated/resources/graphql'

import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import LegacyPlanHintModal from 'modules/product_updates/components/LegacyPlanHintModal'
import { isValidFeature, isValidModule } from 'modules/product_updates/utils/typesValidation'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type Props = {
  chargebeeModuleName: string
  featureIdentifier?: string
  label?: string
  version?: 'factorial-one' | 'factorial'
  source?: string
  moreInfo?: string
  disableIcon?: boolean
  pageSource?: string
}

const UpsellButtons = ({
  chargebeeModuleName,
  featureIdentifier,
  pageSource,
  version,
  source,
  label,
}: Props) => {
  const [isLegacyModalOpen, setIsLegacyModalOpen] = useState(false)
  const isModuleAcquired: boolean = useCheckModuleAcquired(chargebeeModuleName)
  const isFeatureRelatedAcquired = useModuleAvailability(featureIdentifier || '').moduleAvailability
    .available

  const { data: upsellData } = useQuery(ProductUpdatesPoliciesDocument)
  const canSeeUpsellButton = upsellData?.productUpdates._policies.upsellButtonSeeUpsellButton.can
  const canSeeModules = upsellData?.modules._policies.moduleManageModule.can

  if (isModuleAcquired) {
    return version === 'factorial-one' ? (
      <F0Button
        variant="outline"
        label={i18n.t('product_updates.in_your_plan')}
        icon={Check}
        disabled
      />
    ) : null
  }

  if (
    !canSeeUpsellButton ||
    (isModuleAcquired && !!featureIdentifier && isFeatureRelatedAcquired)
  ) {
    return null
  }

  const buttonLabel = canSeeModules
    ? label || i18n.t('product_updates.get_this_feature')
    : i18n.t('product_updates.upgrade_legacy_plan.cta')

  const content = () => {
    const moduleRequested = isValidModule(chargebeeModuleName) ? chargebeeModuleName : undefined

    if (!moduleRequested) return null

    if (isModuleAcquired && !!featureIdentifier && !isFeatureRelatedAcquired) {
      return {
        moduleRequested: moduleRequested,
        requestReason: ValidReasons.MissingFeature,
        requestSource: source || '',
        featureRequested: isValidFeature(featureIdentifier) ? featureIdentifier : undefined,
        pageSource: pageSource || '',
        label: buttonLabel,
      } as const
    }

    return {
      moduleRequested: moduleRequested,
      requestReason: ValidReasons.MissingAddon,
      requestSource: source || '',
      pageSource: pageSource || '',
      label: buttonLabel,
      showModuleModal: true,
      variant: 'outlinePromote',
    } as const
  }

  const upsellContent = content()
  if (!upsellContent) return null

  return (
    <>
      <UpsellingKit.UpsellButton {...upsellContent} />
      {isLegacyModalOpen && <LegacyPlanHintModal onClose={() => setIsLegacyModalOpen(false)} />}
    </>
  )
}

export default UpsellButtons
